import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { AnimatePresence, motion } from "framer-motion";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setIsMobileMenuOpen(false); }, [location.pathname]);

  const navLinks = [
    { href: "#about", label: "About", section: "about" },
    { href: "#services", label: "Services", section: "services" },
    { href: "/gallery", label: "Gallery", isRoute: true },
    { href: "/case-watch", label: "Case Watch", isRoute: true },
    { href: "#why-us", label: "Who We Serve", section: "why-us" },
    { href: "#contact", label: "Contact", section: "contact" },
  ];

  const handleNavClick = (link) => {
    setIsMobileMenuOpen(false);
    if (link.isRoute) { navigate(link.href); return; }
    if (isHomePage) {
      const el = document.querySelector(link.href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/" + link.href);
    }
  };

  const handleBookConsultation = () => {
    setIsMobileMenuOpen(false);
    if (isHomePage) {
      const el = document.querySelector("#lead-capture");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#lead-capture");
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white shadow-lg border-b border-black/10"
          : "bg-white border-b border-black/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex-shrink-0"
            data-testid="header-logo"
            onClick={() => window.scrollTo(0, 0)}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_mheshe-legal/artifacts/1xa23sin_Mheshe%20Attorneys.png"
              alt="Mheshe Attorneys Inc"
              className="h-12 sm:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation — only on lg+ (1024px) */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link)}
                className="nav-link text-charcoal font-sans text-xs xl:text-sm font-medium tracking-wide uppercase bg-transparent border-none cursor-pointer whitespace-nowrap"
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA — only on lg+ */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+27415821022"
              className="flex items-center text-charcoal hover:text-brand-gold transition-colors"
              data-testid="header-phone"
            >
              <Phone className="h-4 w-4 mr-1.5" />
              <span className="text-sm font-medium">041 582 1022</span>
            </a>
            <Button
              onClick={handleBookConsultation}
              className="btn-gold px-5 py-2 text-xs font-semibold uppercase tracking-wider rounded-none"
              data-testid="header-book-consultation-btn"
            >
              Book Consultation
            </Button>
          </div>

          {/* Mobile/Tablet Menu Button — visible below lg */}
          <div className="flex lg:hidden items-center gap-3">
            <a
              href="tel:+27415821022"
              className="text-charcoal hover:text-brand-gold transition-colors p-2"
              aria-label="Call us"
            >
              <Phone className="h-5 w-5" />
            </a>
            <button
              className="p-2 -mr-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6 text-charcoal" />
              ) : (
                <Menu className="h-6 w-6 text-charcoal" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden overflow-hidden bg-white border-t border-black/10"
          >
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                {navLinks.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => handleNavClick(link)}
                    className="text-charcoal font-sans text-sm font-medium tracking-wide uppercase px-4 py-3 hover:bg-light-grey hover:text-brand-gold transition-colors text-left bg-transparent border-none cursor-pointer rounded-sm"
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {link.label}
                  </button>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-black/10">
                <Button
                  onClick={handleBookConsultation}
                  className="btn-gold w-full py-3 text-sm font-semibold uppercase tracking-wider rounded-none"
                  data-testid="mobile-book-consultation-btn"
                >
                  Book Consultation
                </Button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
